<script>
    import { onMount } from "svelte";

    import { user } from "../../stores";

    import {
        DirectUserRoles,
        userFilterTypes,
    } from "../../Utils/utilEnums.svelte";

    import {
        goBack,
        showError,
        getTitle,
        goToURL,
        getText,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        CardLayout,
        PageHead,
        PageLoading,
    } from "../../Components/Common/index.svelte";

    import { cacheDefaultFilters } from "../../Utils/utilCache.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isSubmitBtnDisabled =
        !userFilter.name || !returnedDoctors.some((doctor) => doctor.checked);

    let isLoadingFiltersData = false;

    let isLoadingDoctors = false,
        returnedDoctors = [];

    let isUpdating = false,
        userFilter = {};

    onMount(async () => {
        if ((!isUserAdmin && !isUserManager) || !params) goBack();
        else {
            await loadFilterData();
            await loadDoctors();
        }
    });

    const loadFilterData = async () => {
        isLoadingFiltersData = true;

        await apiMethods.getMethod
            .getDefaultFilterById(params.id)
            .then((res) => _handleLoadFilterData(res));

        isLoadingFiltersData = false;
    };

    const loadDoctors = async () => {
        isLoadingDoctors = true;

        let searchParams = isUserManager
            ? {
                  "filter[groupID]": $user.groupId,
              }
            : userFilter.groupId
              ? {
                    "filter[groupID]": userFilter.groupId,
                }
              : {};

        await apiMethods.getMethod
            .getAccount(0, 999999, searchParams)
            .then((res) => _handleLoadDoctors(res));

        isLoadingDoctors = false;
    };

    const updateFilter = async () => {
        isUpdating = true;

        _syncFilterData();

        await apiMethods.putMethod
            .updateDefaultFilter(userFilter)
            .then((res) => _handleUpdateFilter(res));

        isUpdating = false;
    };

    const toggleSelectDoctor = (doctorId) => {
        const targetDoctor = returnedDoctors.find(
            (doctor) => doctor.id == doctorId,
        );

        if (!targetDoctor) return;

        returnedDoctors = returnedDoctors.map((doctor) =>
            doctor.id === doctorId
                ? { ...doctor, checked: targetDoctor.checked }
                : doctor,
        );
    };

    // HELPER FUNCTIONS
    const _handleLoadFilterData = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load filter data, please refresh the page and try again and contact support if the issue persists.",
            );
        else userFilter = { ...res };
    };

    const _handleLoadDoctors = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load doctors, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            const selectedDoctorIds = userFilter.filter
                ? userFilter.filter.split(",")
                : [];

            res.results.map((doctor) =>
                returnedDoctors.push({
                    id: doctor.id,
                    name: `${getTitle(doctor.title)} ${doctor.fullName}`,
                    checked: selectedDoctorIds.length
                        ? selectedDoctorIds.find((d) => d == doctor.id)
                        : false,
                }),
            );

            returnedDoctors = [...returnedDoctors];
        }
    };

    const _handleUpdateFilter = async (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to update filter, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            await cacheDefaultFilters();

            goToURL(`/DefaultFilters`);
        }
    };

    const _syncFilterData = () => {
        const selectedDoctorIds = [];

        returnedDoctors.map((doctor) => {
            if (doctor.checked) selectedDoctorIds.push(doctor.id);
        });

        userFilter.filter = selectedDoctorIds.join(",");
    };
</script>

<PageHead title="Edit Filters" />

<div class="relative flex-1">
    <div class="absolute inset-0 overflow-y-auto">
        <CardLayout title="Filter Details">
            <div
                class="flex flex-col px-2 py-4 w-full sticky top-0 z-[1] bg-white lg:flex-row"
            >
                <div class="flex flex-col flex-1 gap-y-4 h-auto">
                    <div class="flex gap-x-4 items-center align-center">
                        <label for="name" class="w-1/3 text-right">
                            Name
                        </label>

                        <input
                            type="text"
                            required
                            id="name"
                            name="name"
                            class="input input-bordered input-sm flex flex-1"
                            bind:value={userFilter.name}
                        />
                    </div>

                    <div class="flex gap-x-4 items-center align-center">
                        <label for="type" class="w-1/3 text-right">
                            Type
                        </label>

                        <select
                            id="type"
                            name="type"
                            bind:value={userFilter.filterType}
                            class="input input-bordered input-sm flex flex-1"
                        >
                            {#each userFilterTypes as { id, type }}
                                <option value={id}>{type}</option>
                            {/each}
                        </select>
                    </div>

                    {#if isUserAdmin}
                        <div class="flex gap-x-4 items-center align-center">
                            <label for="type" class="w-1/3 text-right">
                                Group
                            </label>

                            <p class="flex-1 w-2/3">
                                {getText(userFilter.groupName, "-")}
                            </p>
                        </div>
                    {/if}
                </div>

                <div class="divider lg:divider-horizontal" />

                <div
                    class="flex flex-1 items-end justify-end px-4 gap-4 h-auto"
                >
                    <Button clickFunc={goBack} manualClass="w-[64px] btn-error">
                        Cancel
                    </Button>

                    <Button
                        clickFunc={updateFilter}
                        manualClass={isSubmitBtnDisabled
                            ? "w-[64px] text-primary"
                            : "w-[64px]"}
                        isDisabled={isSubmitBtnDisabled}
                    >
                        Update
                    </Button>
                </div>
            </div>

            <div class="divider px-4">CHOOSE AUTO-SEARCH DOCTORS</div>

            {#if isLoadingDoctors}
                <PageLoading />
            {:else}
                <div class="flex flex-1 px-4">
                    <div class="grid grid-cols-3 flex-1 gap-4">
                        {#each returnedDoctors as { id, name, checked }}
                            <div class="flex flex-1 gap-x-2 items-center pl-8">
                                <input
                                    type="checkbox"
                                    id={`doctor-${id}`}
                                    class="checkbox checkbox-sm"
                                    bind:checked
                                    on:change={() => toggleSelectDoctor(id)}
                                />

                                <label
                                    for={`doctor-${id}`}
                                    class="cursor-pointer"
                                >
                                    {name}
                                </label>
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        </CardLayout>
    </div>
</div>
